/**
 * English: Signing in and getting started
 *
 * What this file covers: Sign in, sign up, password reset, two-step sign-in, email verification, the data
 * processing agreement and the first-run onboarding.
 *
 * Tone: Warm, calm and reassuring, in very plain words: this is a business owner's first impression. Security
 * messages stay exact (a wrong code is wrong, an expired link is expired). The agreement text is legal:
 * translate it faithfully, never shorten or soften it.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const signIn: Catalog = {
  'auth.layout.tagline': 'Conversations, broadcasts, automation and billing in one console.',
  'auth.common.password': 'Password',
  'auth.common.codeMismatch': 'That code did not match.',
  'auth.common.backToSignIn': 'Back to sign-in',
  'auth.login.description': 'Sign in to your RepletoryReach account.',
  'auth.login.newHere': 'New here?',
  'auth.login.createAccount': 'Create an account',
  'auth.login.forgotPassword': 'Forgot password?',
  'auth.login.failed': 'Sign-in failed.',
  'auth.login.twofa.hint': 'Enter the 6-digit code from your authenticator app, or a recovery code.',
  'auth.login.twofa.form.code': 'Authentication code',
  'auth.login.twofa.verify': 'Verify',
  'auth.signup.title': 'Create your account',
  'auth.signup.description': 'Set up your organisation and start messaging in minutes.',
  'auth.signup.haveAccount': 'Already have an account?',
  'auth.signup.form.orgName': 'Organisation name',
  'auth.signup.form.orgNamePlaceholder': 'Acme Coffee Roasters',
  'auth.signup.form.workEmail': 'Work email',
  'auth.signup.form.passwordHint': 'At least 12 characters. A short sentence of your own works well; no symbols needed.',
  'auth.signup.submit': 'Create account',
  'auth.signup.failed': 'Sign-up failed.',
  'auth.forgot.title': 'Reset your password',
  'auth.forgot.description': 'We\'ll email you a link to choose a new password.',
  'auth.forgot.submit': 'Send reset link',
  'auth.forgot.sent.title': 'Check your inbox',
  'auth.forgot.sent.body': 'If an account exists for {{email}}, a password-reset link is on its way. The link expires in 1 hour.',
  'auth.reset.title': 'Choose a new password',
  'auth.reset.description': 'Pick a strong password for your account.',
  'auth.reset.form.newPassword': 'New password',
  'auth.reset.form.confirmPassword': 'Confirm password',
  'auth.reset.submit': 'Reset password',
  'auth.reset.errors.tooShort': 'Password must be at least 12 characters.',
  'auth.reset.errors.mismatch': 'Passwords do not match.',
  'auth.reset.errors.invalidLink': 'This reset link is invalid. Request a new one.',
  'auth.reset.failed': 'Could not reset the password.',
  'auth.reset.toast.done': 'Your password has been reset. Sign in with your new password.',
  'auth.reset.toast.doneTitle': 'Password reset',
  'auth.verify.title': 'Verify your email',
  'auth.verify.description': 'Enter the 6-digit code we emailed you.',
  'auth.verify.form.code': '6-digit code',
  'auth.verify.submit': 'Verify email',
  'auth.verify.resend': 'Resend code',
  'auth.verify.codeHint': 'The code expires 15 minutes after it\'s sent.',
  'auth.verify.backToDashboard': 'Back to dashboard',
  'auth.verify.toast.done': 'Your email is verified.',
  'auth.verify.toast.resent': 'A fresh code is on its way.',
  'auth.verify.toast.resentTitle': 'Code sent',
  'auth.verify.toast.resendFailed': 'Could not resend.',
  'auth.verify.toast.resendFailedTitle': 'Resend failed',
  'auth.mfa.title': 'Two-factor sign-in',
  'auth.mfa.description': 'Continue your sign-in from the login screen: you\'ll be prompted for your authentication code there.',
  'auth.mfa.goToSignIn': 'Go to sign-in',
  'onboarding.title': 'Welcome to RepletoryReach',
  'onboarding.description': 'A few quick steps and you\'ll be ready to message your customers on WhatsApp.',
  'onboarding.stepIndicator': 'Step {{current}} of {{total}} · {{label}}',
  'onboarding.skipSetup': 'Skip setup',
  'onboarding.skipStep': 'Skip this step',
  'onboarding.toast.codeResent': 'A new code is on its way to {{email}}.',
  'onboarding.steps.number.returnHint': 'Your place is saved, so you can attach a number and come back here.',
  'onboarding.steps.verify.label': 'Verify email',
  'onboarding.steps.verify.title': 'Verify your email',
  'onboarding.steps.verify.body': 'We emailed a 6-digit code to {{email}}.',
  'onboarding.steps.verify.form.code': 'Verification code',
  'onboarding.steps.verify.resend': 'Resend code',
  'onboarding.steps.verify.skip': 'Skip for now',
  'onboarding.steps.verify.confirm': 'Verify',
  'onboarding.steps.region.label': 'Region',
  'onboarding.steps.region.title': 'Where do you operate?',
  'onboarding.steps.region.description': 'This sets your currency, timezone and payment gateway.',
  'onboarding.steps.region.form.country': 'Country',
  'onboarding.steps.region.country.za': 'South Africa (ZAR)',
  'onboarding.steps.region.country.ng': 'Nigeria (NGN)',
  'onboarding.steps.region.country.ke': 'Kenya (KES)',
  'onboarding.steps.name.label': 'You and your business',
  'onboarding.steps.name.title': 'What should we call you and your business?',
  'onboarding.steps.name.form.displayName': 'Your name',
  'onboarding.steps.name.form.displayNamePlaceholder': 'e.g. Thandi N.',
  'onboarding.steps.number.label': 'WhatsApp number',
  'onboarding.steps.number.title': 'Attach a WhatsApp number',
  'onboarding.steps.number.description': 'Connect your Meta phone number so you can send and receive. You can do this now or any time from the Numbers page.',
  'onboarding.steps.number.notice': 'Meta Embedded Signup (self-service connect) lands once your Meta Business app is approved. Until then attach a number manually with its Meta phone-number-id.',
  'onboarding.steps.number.openNumbers': 'Open Numbers',
  'onboarding.steps.done.label': 'Done',
  'onboarding.steps.done.title': 'You\'re all set',
  'onboarding.steps.done.description': 'Your console is ready. Import contacts, create a template and run your first broadcast: the dashboard checklist will guide you.',
  'onboarding.steps.done.goToDashboard': 'Go to dashboard',
  'onboarding.toast.emailVerified': 'Email verified.',
  'onboarding.toast.emailVerifiedTitle': 'Onboarding',
  'onboarding.toast.codeMismatch': 'Code did not match.',
  'onboarding.toast.codeMismatchTitle': 'Verification',
  'onboarding.toast.saveFailed': 'Could not save.',
  'onboarding.toast.profileFailedTitle': 'Profile',
  'onboarding.stepOf': 'Step {{step}} of {{total}}',
  'onboarding.stepLabel': 'Step',
  'onboarding.steps.verify.sentTo': 'We emailed a 6-digit code to {{email}}.',
  'auth.reset.errors.weak': 'That password does not meet all the requirements below.',
  'auth.login.errors.badCredentials': 'That email and password combination doesn\'t match an account. Check both and try again.',
  'auth.login.errors.wrongDomain': 'This sign-in is for business accounts. Platform staff sign in at the admin console instead.',
  'auth.login.errors.unavailable': 'We couldn\'t reach the sign-in service. Check your connection and try again.',
  'auth.login.errors.badCode': 'That code isn\'t right. Enter the current 6-digit code, or use a recovery code.',
  'auth.verify.resent': 'Verification email sent: check your inbox.',
  'auth.layout.docs': 'Documentation',
  'auth.layout.status': 'System status',
  'auth.layout.support': 'Support',
  'auth.layout.copyright': '© {{year}} RepletoryReach. All rights reserved.',
  'auth.layout.theme': 'Theme',
  'auth.login.wrongDomain': 'This account signs in on the RepletoryReach admin console, not here.',
  'auth.login.tooMany': 'Too many attempts. Wait a minute and try again.',
  'auth.login.offline': 'We couldn\'t reach the sign-in service. Check your connection and try again.',
  'auth.login.emailPlaceholder': 'you@example.com',
  'auth.login.passwordPlaceholder': 'Enter your password',
  'auth.login.useRecoveryCode': 'Use a recovery code',
  'auth.login.useAuthCode': 'Use an authenticator code',
  'auth.login.recoveryCodeLabel': 'Recovery code',
  'auth.signup.form.name': 'Your name',
  'auth.signup.form.namePlaceholder': 'Thandi Mokoena',
  'auth.signup.form.country': 'Country',
  'auth.signup.emailTaken': 'That email already has an account.',
  'auth.signup.emailTaken.signIn': 'Sign in',
  'auth.signup.emailTaken.reset': 'Reset your password',
  'auth.forgot.errors.unavailable': 'We couldn\'t send the email just now. Check your connection and try again.',
  'auth.forgot.sent.changeEmail': 'Use a different email',
  'auth.forgot.sent.resend': 'Send it again',
  'auth.forgot.sent.resendIn': 'Send it again in {{seconds}}s',
  'auth.reset.form.passwordHint': 'At least 12 characters. A short sentence of your own works well; no symbols needed.',
  'auth.reset.errors.requestNew': 'Request a new link',
  'auth.reset.errors.emailMismatch': 'That email doesn\'t match this reset link.',
  'auth.reset.expiryNote': 'Reset links expire one hour after they are sent.',
  'auth.verify.resendIn': 'Resend in {{seconds}}s',
  'auth.verify.alreadyVerified': 'Your email address is already verified.',
  'auth.verify.spamHint': 'No email? Check your spam folder.',
  'auth.mfa.backToSignIn': 'Go to sign-in',
  'auth.common.or': 'or',
  'auth.login.title': 'Welcome back 👋',
  'auth.layout.headline': 'Every WhatsApp conversation your team has, in one place.',
  'auth.layout.feature.inbox.title': 'One shared inbox',
  'auth.layout.feature.inbox.body': 'Every number your business runs, in a single thread view. Assign, transfer and track response times.',
  'auth.layout.feature.broadcasts.title': 'Broadcasts that respect opt-out',
  'auth.layout.feature.broadcasts.body': 'Segment your contacts, throttle the send, and let STOP take someone off the list automatically.',
  'auth.layout.feature.automation.title': 'Answers without an agent',
  'auth.layout.feature.automation.body': 'Build a flow, ground it on your own documents, and hand over to a person the moment it is out of its depth.',
  'auth.layout.feature.billing.title': 'Pay for messages, not seats',
  'auth.layout.feature.billing.body': 'A credit wallet with a full ledger, VAT-compliant invoices and local payment methods.',
  'auth.layout.trust': 'Built for South African teams. POPIA-aware consent tracking, ZAR billing and SARS-compliant tax invoices.',
  'auth.social.or': 'or',
  'auth.social.continueWith': 'Continue with {{provider}}',
  'auth.social.provider': 'your provider',
  'auth.social.hint': 'We only ever receive your name and email address, and only after you agree to it.',
  'auth.callback.signingIn': 'Signing you in…',
  'auth.callback.failedTitle': 'That sign-in did not finish',
  'auth.callback.failedLead': 'Nothing has changed on your account. Try again, or sign in with your email address and password.',
  'auth.callback.backToSignIn': 'Back to sign in',
  'auth.callback.error.STATE_INVALID': 'That sign-in link had already been used or had expired. Please start again.',
  'auth.callback.error.PROVIDER_REFUSED': 'You cancelled the sign-in at {{provider}}. Nothing was shared with us.',
  'auth.callback.error.PROVIDER_DISABLED': 'Signing in with {{provider}} has been switched off. Please use your email address and password.',
  'auth.callback.error.NO_CODE': '{{provider}} sent us back without an answer. Please try again.',
  'auth.callback.error.TOKEN_EXCHANGE_FAILED': 'We could not complete the sign-in with {{provider}}. Please try again in a moment.',
  'auth.callback.error.NO_ID_TOKEN': '{{provider}} did not confirm who you are. Please try again.',
  'auth.callback.error.ID_TOKEN_INVALID': 'We could not verify the answer from {{provider}}, so we stopped. Please try again.',
  'auth.callback.error.NONCE_MISMATCH': 'We could not match that sign-in to the one you started. Please start again.',
  'auth.callback.error.PROVIDER_UNREACHABLE': '{{provider}} could not be reached. Please try again in a moment.',
  'auth.callback.error.EMAIL_REQUIRED': 'Your {{provider}} account has no email address on it, and we need one. Please sign up with your email address instead.',
  'auth.callback.error.EMAIL_NOT_VERIFIED': '{{provider}} has not confirmed that email address yet. Confirm it there, then try again.',
  'auth.callback.error.DOMAIN_NOT_ALLOWED': 'Sign-in is limited to certain email domains, and yours is not one of them.',
  'auth.callback.error.LINK_REQUIRES_VERIFICATION': 'An account already uses that email address but has not confirmed it yet. Sign in with your password and confirm your email, then you can use {{provider}}.',
  'auth.callback.error.WRONG_DOMAIN': 'That account cannot sign in here.',
  'auth.callback.error.ACCOUNT_SUSPENDED': 'That account has been suspended. Please ask your administrator.',
  'auth.callback.error.PLATFORM_SIGNUP_BLOCKED': 'Staff accounts cannot be created this way.',
  'auth.callback.error.SIGNUP_FAILED': 'We could not create your account. Please sign up with your email address instead.',
  'auth.callback.error.SIGN_IN_FAILED': 'That sign-in did not work. Please try again, or use your email address and password.',
  'auth.callback.error.EXCHANGE_FAILED': 'That sign-in link had already been used or had expired. Please start again.',
  'auth.signup.form.referralCode': 'Referral code',
  'auth.signup.form.referralCodeHint': 'If somebody introduced you, put their code here. Leave it empty if not.',
  'auth.signup.form.referralCodePlaceholder': 'For example THAN7K4Q',
  'auth.signup.form.referredBy': 'Referred by {{name}}.',
  'auth.signup.form.referralUnknown': 'We do not recognise that code. You can still carry on without it.',
  'agreements.dpa.title': 'Before you start: our data processing agreement',
  'agreements.dpa.titleUpdated': 'Our data processing agreement has been updated',
  'agreements.dpa.why': 'Your contacts, conversations and call recordings are your customers\' personal information. Under POPIA we look after it on your behalf, and this agreement sets out how: what we do with it, how we keep it safe, and what happens to it when you leave.',
  'agreements.dpa.updated': 'Updated on {{date}}. Please read it and accept it again to carry on.',
  'agreements.dpa.read': 'Read the data processing agreement (version {{version}})',
  'agreements.dpa.confirm': 'I have read the data processing agreement and accept it on behalf of {{org}}.',
  'agreements.dpa.accept': 'Accept and continue',
  'agreements.failed': 'Could not record your acceptance.',
  'agreements.impersonating': 'Someone in the organisation has to accept this. A platform operator looking in cannot accept it for them.',
  'agreements.impersonatingBanner': 'This organisation has not yet accepted the current data processing agreement. Its people are asked to before they can use the console.',
  'onboarding.steps.agreement.label': 'Agreement',
  'onboarding.steps.agreement.title': 'Accept our data processing agreement',
  'onboarding.steps.agreement.done': 'Accepted on {{date}} by {{who}}.',
  'agreements.agentWaits': 'An owner or admin in your organisation has to accept this before the console can be used. Let one of them know.',
  'auth.callback.codeTitle': 'One more step',
  'auth.callback.error.TWO_FACTOR_SETUP_REQUIRED': 'This account must set up two-step sign-in first. Sign in with your email and password, and you will be shown how.',
  'onboarding.steps.name.form.businessName': 'Business name',
  'onboarding.steps.name.form.businessNameHint': 'How customers know you. It appears on invoices and in messages, and you can change it later under Settings, Organisation.',
  'onboarding.steps.name.form.businessNamePlaceholder': 'e.g. Sunrise Salon'
};

export default signIn;
